export * from './core/types'
export * from './core/error'
export * from './core/query'
export * from './core/client'
export * from './bridge'
export * from './payment-fact'
export * from './domains/decoration'
export * from './domains/mall'
export * from './domains/feed'
export * from './domains/fulfillment'
export * from './domains/alumni'
export * from './domains/guoxue'
export * from './domains/system'
export * from './domains/crm'
export * from './domains/erp'
export * from './domains/member'
export * from './domains/traffic'
export * from './domains/otahub'
export * from './workbench-analytics'
export * from './dimension-analytics'
export * from './crash-analytics'

import { CoreApiClient } from './core/client'
import {
  DecorationApiModule,
  type DecorationPlatform,
  type DecorationPageQuery,
  type DecorationPageQueryParams,
  type DecorationTemplateQueryParams,
  type SaveDecorationPageRequest,
  type CopyDecorationPageRequest,
  type SaveDecorationTemplateRequest,
  type InstallDecorationTemplateRequest,
  type UpdateDecorationTemplateStatusRequest,
  type SaveDecorationTemplateVersionRequest,
  type UpdateDecorationTemplateVersionStatusRequest,
  type InstallDecorationTemplateVersionRequest,
  type PreviewDecorationTemplateUpgradeRequest,
  type ApplyDecorationTemplateUpgradeRequest,
  type LegacyDecorationImportRequest,
  type DecorationMarketTemplate
} from './domains/decoration'
import { MallApiModule } from './domains/mall'
import { FeedApiModule } from './domains/feed'
import { FulfillmentApiModule } from './domains/fulfillment'
import { AlumniApiModule } from './domains/alumni'
import { GuoxueApiModule } from './domains/guoxue'
import { ErpApiModule } from './domains/erp'
import { MemberApiModule } from './domains/member'
import { SystemApiModule } from './domains/system'
import type { RequestOptions, PageResult } from './core/types'

export interface DecorationPage {
  id: number
  tenantId: number
  moduleCode: string
  pageCode: string
  pageTitle: string
  pageType: string
  routePath: string
  status: string
  published: boolean
  schemaJson: string
  publishedSchemaJson: string
  themeJson: string
  remark: string
  createdAt: string
  updatedAt: string
  publishedAt?: string | null
}

export interface ClientDecorationPageView {
  id: number
  tenantId: number
  moduleCode: string
  pageCode: string
  pageTitle: string
  pageType: string
  routePath: string
  published: boolean
  publishedSchemaJson: string
  publishedAt?: string | null
  supportedPlatforms?: DecorationPlatform[] | null
  supportedPlatformsJson?: string | null
}

export type PublishedDecorationPage = ClientDecorationPageView

export interface DecorationPageQrCode {
  pageCode: string
  publicUrl: string
  imageUrl: string
  h5Url?: string
  miniAppId?: string
  miniAppPagePath?: string
  webUrl?: string | null
  channel?: string | null
  platform?: DecorationPlatform | null
  provenance?: string | null
  status: string
  publishedAt?: string | null
}

export interface DecorationPagePreviewMetadata {
  h5Url?: string
  miniAppId?: string
  miniAppPagePath?: string
  webUrl?: string | null
  channel?: string | null
  platform?: DecorationPlatform | null
  provenance?: string | null
}

export interface DecorationComponentDefinition {
  type: string
  label: string
  groupName: string
  description: string
  icon: string
  enabled: boolean
  sortOrder: number
  defaultPropsJson: string
  propertySchemaJson: string
  styleSchemaJson: string
  dataSourceSchemaJson: string
  componentScope: string
  ownerModuleCode: string
  supportedPlatformsJson: string
  protocolVersion: string
}

export interface DecorationPageReference {
  sourceModuleCode: string
  sourcePageCode: string
  sourcePageTitle: string
  sourceComponentId?: string | null
  sourcePath: string
  targetModuleCode: string
  targetPageCode: string
}

export interface DecorationLinkTargetDefinition {
  targetCode: string
  targetName: string
  moduleCode: string
  groupName: string
  targetType: string
  pageCode: string
  routePath: string
  requiredPermission: string
  loginRequired: boolean
  enabled: boolean
  sortOrder: number
  paramsSchemaJson: string
  supportedPlatformsJson: string
}

export interface DecorationNavigationTarget {
  targetCode: string
  targetName?: string
  moduleCode: string
  targetType: string
  pageCode: string
  routePath: string
  routeTemplate?: string
  params?: Record<string, string | number | boolean>
  paramBindings?: Record<string, string>
  requiredPermission?: string
  loginRequired?: boolean
}

/**
 * 统一 API 客户端
 * 聚合了系统、商城、校友会、国学、ERP、会员能力、装修等所有领域方法
 */
export class ApiClient extends CoreApiClient {
  private readonly _decoration: DecorationApiModule
  private readonly _mall: MallApiModule
  private readonly _feed: FeedApiModule
  private readonly _fulfillment: FulfillmentApiModule
  private readonly _alumni: AlumniApiModule
  private readonly _guoxue: GuoxueApiModule
  private readonly _erp: ErpApiModule
  private readonly _member: MemberApiModule
  private readonly _system: SystemApiModule

  constructor(options: RequestOptions = {}) {
    super(options)
    this._decoration = new DecorationApiModule(this)
    this._mall = new MallApiModule(this)
    this._feed = new FeedApiModule(this)
    this._fulfillment = new FulfillmentApiModule(this)
    this._alumni = new AlumniApiModule(this)
    this._guoxue = new GuoxueApiModule(this)
    this._erp = new ErpApiModule(this)
    this._member = new MemberApiModule(this)
    this._system = new SystemApiModule(this)
  }

  // ==================== System & Agent ====================
  chatPlatformAgent(body: Parameters<SystemApiModule['chatPlatformAgent']>[0]) {
    return this._system.chatPlatformAgent(body)
  }

  getPlatformAgentModelConfig() {
    return this._system.getPlatformAgentModelConfig()
  }

  savePlatformAgentModelConfig(body: Parameters<SystemApiModule['savePlatformAgentModelConfig']>[0]) {
    return this._system.savePlatformAgentModelConfig(body)
  }

  testPlatformAgentModel(message = '') {
    return this._system.testPlatformAgentModel(message)
  }

  getPlatformAgentModelStatus() {
    return this._system.getPlatformAgentModelStatus()
  }

  chatSaasAgent(body: Parameters<SystemApiModule['chatSaasAgent']>[0]) {
    return this._system.chatSaasAgent(body)
  }

  getSaasAgentModelStatus() {
    return this._system.getSaasAgentModelStatus()
  }

  getAgentTools() {
    return this._system.getAgentTools()
  }

  invokeAgentTool(toolCode: string, body?: Parameters<SystemApiModule['invokeAgentTool']>[1]) {
    return this._system.invokeAgentTool(toolCode, body)
  }

  // ==================== Decoration: Client Projections ====================
  getClientDecorationPage(pageCode = 'home'): Promise<ClientDecorationPageView> {
    return this._decoration.getClientDecorationPage(pageCode)
  }

  getClientModuleDecorationPage(moduleCode: string, pageCode = 'home'): Promise<ClientDecorationPageView> {
    return this._decoration.getClientModuleDecorationPage(moduleCode, pageCode)
  }

  getClientContentDecorationPage(siteId: number, pageCode = 'home'): Promise<ClientDecorationPageView> {
    return this._decoration.getClientContentDecorationPage(siteId, pageCode)
  }

  getClientMallDecorationPage(pageCode = 'home'): Promise<ClientDecorationPageView> {
    return this._decoration.getClientMallDecorationPage(pageCode)
  }

  // ==================== Decoration: SaaS System ====================
  getSaasDecorationPages(query?: DecorationPageQueryParams): Promise<PageResult<DecorationPage>> {
    return this._decoration.getSaasDecorationPages(query)
  }

  getSaasDecorationPage(pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.getSaasDecorationPage(pageCode)
  }

  getSaasDecorationPageReferences(pageCode = 'home') {
    return this._decoration.getSaasDecorationPageReferences(pageCode)
  }

  saveSaasDecorationPage(pageCode: string, body: SaveDecorationPageRequest): Promise<DecorationPage> {
    return this._decoration.saveSaasDecorationPage(pageCode, body)
  }

  copySaasDecorationPage(pageCode: string, body: CopyDecorationPageRequest) {
    return this._decoration.copySaasDecorationPage(pageCode, body)
  }

  deleteSaasDecorationPage(pageCode: string) {
    return this._decoration.deleteSaasDecorationPage(pageCode)
  }

  publishSaasDecorationPage(pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.publishSaasDecorationPage(pageCode)
  }

  issueSaasDecorationPageQrCode(pageCode = 'home') {
    return this._decoration.issueSaasDecorationPageQrCode(pageCode)
  }

  getSaasDecorationPagePreviewMetadata(pageCode = 'home') {
    return this._decoration.getSaasDecorationPagePreviewMetadata(pageCode)
  }

  resetSaasDecorationPage(pageCode = 'home') {
    return this._decoration.resetSaasDecorationPage(pageCode)
  }

  validateSaasDecorationPage(pageCode: string, body: { schemaJson: string }) {
    // /validate endpoint
    return this._decoration.validateSaasDecorationPage(pageCode, body)
  }

  getSaasDecorationComponents() {
    return this._decoration.getSaasDecorationComponents()
  }

  getSaasDecorationDataSources() {
    return this._decoration.getSaasDecorationDataSources()
  }

  getSaasDecorationLinkTargets() {
    return this._decoration.getSaasDecorationLinkTargets()
  }

  getSaasDecorationTemplates(query?: DecorationTemplateQueryParams): Promise<PageResult<DecorationMarketTemplate>> {
    return this._decoration.getSaasDecorationTemplates(query)
  }

  saveSaasDecorationTemplate(body: SaveDecorationTemplateRequest) {
    return this._decoration.saveSaasDecorationTemplate(body)
  }

  installSaasDecorationTemplate(templateCode: string, body: InstallDecorationTemplateRequest): Promise<DecorationPage> {
    return this._decoration.installSaasDecorationTemplate(templateCode, body)
  }

  updateSaasDecorationTemplateStatus(templateCode: string, body: UpdateDecorationTemplateStatusRequest) {
    return this._decoration.updateSaasDecorationTemplateStatus(templateCode, body)
  }

  recycleSaasDecorationTemplate(templateCode: string) {
    return this._decoration.recycleSaasDecorationTemplate(templateCode)
  }

  restoreSaasDecorationTemplate(templateCode: string) {
    return this._decoration.restoreSaasDecorationTemplate(templateCode)
  }

  deleteSaasDecorationTemplatePermanently(templateCode: string) {
    return this._decoration.deleteSaasDecorationTemplatePermanently(templateCode)
  }

  getSaasDecorationTemplateBinding(pageCode: string) {
    return this._decoration.getSaasDecorationTemplateBinding(pageCode)
  }

  getSaasDecorationBusinessTemplateVersions(templateCode: string, pageCode?: string) {
    return this._decoration.getSaasDecorationBusinessTemplateVersions(templateCode, pageCode)
  }

  installSaasDecorationBusinessTemplateVersion(
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ): Promise<DecorationPage> {
    return this._decoration.installSaasDecorationBusinessTemplateVersion(templateCode, templateVersion, body)
  }

  previewSaasDecorationTemplateUpgrade(pageCode: string, body: PreviewDecorationTemplateUpgradeRequest) {
    return this._decoration.previewSaasDecorationTemplateUpgrade(pageCode, body)
  }

  upgradeSaasDecorationTemplate(pageCode: string, body: ApplyDecorationTemplateUpgradeRequest): Promise<DecorationPage> {
    return this._decoration.upgradeSaasDecorationTemplate(pageCode, body)
  }

  applySaasDecorationTemplateUpgrade(pageCode: string, body: ApplyDecorationTemplateUpgradeRequest): Promise<DecorationPage> {
    return this._decoration.applySaasDecorationTemplateUpgrade(pageCode, body)
  }

  previewSaasDecorationLegacyImport(body: LegacyDecorationImportRequest) {
    return this._decoration.previewSaasDecorationLegacyImport(body)
  }

  importSaasDecorationLegacy(body: LegacyDecorationImportRequest) {
    return this._decoration.importSaasDecorationLegacy(body)
  }

  revertSaasDecorationTemplate(pageCode: string) {
    return this._decoration.revertSaasDecorationTemplate(pageCode)
  }

  // ==================== Decoration: SaaS Module ====================
  getSaasModuleDecorationPages(moduleCode: string, query?: DecorationPageQueryParams): Promise<PageResult<DecorationPage>> {
    return this._decoration.getSaasModuleDecorationPages(moduleCode, query)
  }

  getSaasModuleDecorationPage(moduleCode: string, pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.getSaasModuleDecorationPage(moduleCode, pageCode)
  }

  getSaasModuleDecorationPageReferences(moduleCode: string, pageCode = 'home') {
    return this._decoration.getSaasModuleDecorationPageReferences(moduleCode, pageCode)
  }

  saveSaasModuleDecorationPage(moduleCode: string, pageCode: string, body: SaveDecorationPageRequest): Promise<DecorationPage> {
    return this._decoration.saveSaasModuleDecorationPage(moduleCode, pageCode, body)
  }

  copySaasModuleDecorationPage(moduleCode: string, pageCode: string, body: CopyDecorationPageRequest) {
    return this._decoration.copySaasModuleDecorationPage(moduleCode, pageCode, body)
  }

  deleteSaasModuleDecorationPage(moduleCode: string, pageCode: string) {
    return this._decoration.deleteSaasModuleDecorationPage(moduleCode, pageCode)
  }

  publishSaasModuleDecorationPage(moduleCode: string, pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.publishSaasModuleDecorationPage(moduleCode, pageCode)
  }

  issueSaasModuleDecorationPageQrCode(moduleCode: string, pageCode = 'home') {
    return this._decoration.issueSaasModuleDecorationPageQrCode(moduleCode, pageCode)
  }

  getSaasModuleDecorationPagePreviewMetadata(moduleCode: string, pageCode = 'home') {
    return this._decoration.getSaasModuleDecorationPagePreviewMetadata(moduleCode, pageCode)
  }

  resetSaasModuleDecorationPage(moduleCode: string, pageCode = 'home') {
    return this._decoration.resetSaasModuleDecorationPage(moduleCode, pageCode)
  }

  getSaasModuleDecorationComponents(moduleCode: string) {
    return this._decoration.getSaasModuleDecorationComponents(moduleCode)
  }

  getSaasModuleDecorationDataSources(moduleCode: string) {
    return this._decoration.getSaasModuleDecorationDataSources(moduleCode)
  }

  getSaasModuleDecorationLinkTargets(moduleCode: string) {
    return this._decoration.getSaasModuleDecorationLinkTargets(moduleCode)
  }

  getSaasModuleDecorationTemplates(
    moduleCode: string,
    query?: DecorationTemplateQueryParams
  ): Promise<PageResult<DecorationMarketTemplate>> {
    return this._decoration.getSaasModuleDecorationTemplates(moduleCode, query)
  }

  saveSaasModuleDecorationTemplate(moduleCode: string, body: SaveDecorationTemplateRequest) {
    return this._decoration.saveSaasModuleDecorationTemplate(moduleCode, body)
  }

  installSaasModuleDecorationTemplate(
    moduleCode: string,
    templateCode: string,
    body: InstallDecorationTemplateRequest
  ): Promise<DecorationPage> {
    return this._decoration.installSaasModuleDecorationTemplate(moduleCode, templateCode, body)
  }

  updateSaasModuleDecorationTemplateStatus(
    moduleCode: string,
    templateCode: string,
    body: UpdateDecorationTemplateStatusRequest
  ) {
    return this._decoration.updateSaasModuleDecorationTemplateStatus(moduleCode, templateCode, body)
  }

  recycleSaasModuleDecorationTemplate(moduleCode: string, templateCode: string) {
    return this._decoration.recycleSaasModuleDecorationTemplate(moduleCode, templateCode)
  }

  restoreSaasModuleDecorationTemplate(moduleCode: string, templateCode: string) {
    return this._decoration.restoreSaasModuleDecorationTemplate(moduleCode, templateCode)
  }

  deleteSaasModuleDecorationTemplatePermanently(moduleCode: string, templateCode: string) {
    return this._decoration.deleteSaasModuleDecorationTemplatePermanently(moduleCode, templateCode)
  }

  getSaasModuleDecorationTemplateBinding(moduleCode: string, pageCode: string) {
    return this._decoration.getSaasModuleDecorationTemplateBinding(moduleCode, pageCode)
  }

  getSaasModuleDecorationBusinessTemplateVersions(moduleCode: string, templateCode: string, pageCode?: string) {
    return this._decoration.getSaasModuleDecorationBusinessTemplateVersions(moduleCode, templateCode, pageCode)
  }

  installSaasModuleDecorationBusinessTemplateVersion(
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ): Promise<DecorationPage> {
    return this._decoration.installSaasModuleDecorationBusinessTemplateVersion(moduleCode, templateCode, templateVersion, body)
  }

  previewSaasModuleDecorationTemplateUpgrade(
    moduleCode: string,
    pageCode: string,
    body: PreviewDecorationTemplateUpgradeRequest
  ) {
    return this._decoration.previewSaasModuleDecorationTemplateUpgrade(moduleCode, pageCode, body)
  }

  applySaasModuleDecorationTemplateUpgrade(
    moduleCode: string,
    pageCode: string,
    body: ApplyDecorationTemplateUpgradeRequest
  ): Promise<DecorationPage> {
    return this._decoration.applySaasModuleDecorationTemplateUpgrade(moduleCode, pageCode, body)
  }

  // ==================== Decoration: SaaS Mall ====================
  getSaasMallDecorationPages(query?: { keyword?: string; published?: boolean | null }): Promise<DecorationPage[]> {
    return this._decoration.getSaasMallDecorationPages(query)
  }

  getSaasMallDecorationPage(pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.getSaasMallDecorationPage(pageCode)
  }

  saveSaasMallDecorationPage(pageCode: string, body: SaveDecorationPageRequest): Promise<DecorationPage> {
    return this._decoration.saveSaasMallDecorationPage(pageCode, body)
  }

  publishSaasMallDecorationPage(pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.publishSaasMallDecorationPage(pageCode)
  }

  resetSaasMallDecorationPage(pageCode = 'home') {
    return this._decoration.resetSaasMallDecorationPage(pageCode)
  }

  getSaasMallDecorationComponents() {
    return this._decoration.getSaasMallDecorationComponents()
  }

  getSaasMallDecorationDataSources() {
    return this._decoration.getSaasMallDecorationDataSources()
  }

  getSaasMallDecorationLinkTargets() {
    return this._decoration.getSaasMallDecorationLinkTargets()
  }

  getSaasMallDecorationTemplates(query?: { keyword?: string; pageType?: string; publicOnly?: boolean | null }) {
    return this._decoration.getSaasMallDecorationTemplates(query)
  }

  saveSaasMallDecorationTemplate(body: SaveDecorationTemplateRequest) {
    return this._decoration.saveSaasMallDecorationTemplate(body)
  }

  installSaasMallDecorationTemplate(templateCode: string, body: InstallDecorationTemplateRequest): Promise<DecorationPage> {
    return this._decoration.installSaasMallDecorationTemplate(templateCode, body)
  }

  updateSaasMallDecorationTemplateStatus(templateCode: string, body: UpdateDecorationTemplateStatusRequest) {
    return this._decoration.updateSaasMallDecorationTemplateStatus(templateCode, body)
  }

  recycleSaasMallDecorationTemplate(templateCode: string) {
    return this._decoration.recycleSaasMallDecorationTemplate(templateCode)
  }

  restoreSaasMallDecorationTemplate(templateCode: string) {
    return this._decoration.restoreSaasMallDecorationTemplate(templateCode)
  }

  deleteSaasMallDecorationTemplatePermanently(templateCode: string) {
    return this._decoration.deleteSaasMallDecorationTemplatePermanently(templateCode)
  }

  // ==================== Decoration: Admin ====================
  getAdminDecorationPages(query?: DecorationPageQuery): Promise<DecorationPage[]> {
    return this._decoration.getAdminDecorationPages(query)
  }

  getAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.getAdminDecorationPage(tenantId, moduleCode, pageCode)
  }

  saveAdminDecorationPage(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: SaveDecorationPageRequest
  ): Promise<DecorationPage> {
    return this._decoration.saveAdminDecorationPage(tenantId, moduleCode, pageCode, body)
  }

  publishAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home'): Promise<DecorationPage> {
    return this._decoration.publishAdminDecorationPage(tenantId, moduleCode, pageCode)
  }

  resetAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home') {
    return this._decoration.resetAdminDecorationPage(tenantId, moduleCode, pageCode)
  }

  getAdminDecorationComponents(moduleCode: string) {
    return this._decoration.getAdminDecorationComponents(moduleCode)
  }

  saveAdminDecorationComponent(body: Parameters<DecorationApiModule['saveAdminDecorationComponent']>[0]) {
    return this._decoration.saveAdminDecorationComponent(body)
  }

  deleteAdminDecorationComponent(type: string) {
    return this._decoration.deleteAdminDecorationComponent(type)
  }

  getAdminDecorationDataSources(moduleCode: string) {
    return this._decoration.getAdminDecorationDataSources(moduleCode)
  }

  saveAdminDecorationDataSource(body: Parameters<DecorationApiModule['saveAdminDecorationDataSource']>[0]) {
    return this._decoration.saveAdminDecorationDataSource(body)
  }

  deleteAdminDecorationDataSource(sourceCode: string) {
    return this._decoration.deleteAdminDecorationDataSource(sourceCode)
  }

  getAdminDecorationLinkTargets(tenantId: number, moduleCode: string) {
    return this._decoration.getAdminDecorationLinkTargets(tenantId, moduleCode)
  }

  saveAdminDecorationLinkTarget(body: Parameters<DecorationApiModule['saveAdminDecorationLinkTarget']>[0]) {
    return this._decoration.saveAdminDecorationLinkTarget(body)
  }

  deleteAdminDecorationLinkTarget(targetCode: string) {
    return this._decoration.deleteAdminDecorationLinkTarget(targetCode)
  }

  getAdminDecorationTemplates(
    tenantId: number,
    moduleCode: string,
    query?: Parameters<DecorationApiModule['getAdminDecorationTemplates']>[2]
  ) {
    return this._decoration.getAdminDecorationTemplates(tenantId, moduleCode, query)
  }

  saveAdminDecorationTemplate(tenantId: number, moduleCode: string, body: SaveDecorationTemplateRequest) {
    return this._decoration.saveAdminDecorationTemplate(tenantId, moduleCode, body)
  }

  installAdminDecorationTemplate(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    body: InstallDecorationTemplateRequest
  ): Promise<DecorationPage> {
    return this._decoration.installAdminDecorationTemplate(tenantId, moduleCode, templateCode, body)
  }

  updateAdminDecorationTemplateStatus(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    body: UpdateDecorationTemplateStatusRequest
  ) {
    return this._decoration.updateAdminDecorationTemplateStatus(tenantId, moduleCode, templateCode, body)
  }

  recycleAdminDecorationTemplate(tenantId: number, moduleCode: string, templateCode: string) {
    return this._decoration.recycleAdminDecorationTemplate(tenantId, moduleCode, templateCode)
  }

  restoreAdminDecorationTemplate(tenantId: number, moduleCode: string, templateCode: string) {
    return this._decoration.restoreAdminDecorationTemplate(tenantId, moduleCode, templateCode)
  }

  deleteAdminDecorationTemplatePermanently(tenantId: number, moduleCode: string, templateCode: string) {
    return this._decoration.deleteAdminDecorationTemplatePermanently(tenantId, moduleCode, templateCode)
  }

  saveAdminDecorationBusinessTemplateVersion(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: SaveDecorationTemplateVersionRequest
  ) {
    return this._decoration.saveAdminDecorationBusinessTemplateVersion(tenantId, moduleCode, pageCode, body)
  }

  getAdminDecorationTemplateBinding(tenantId: number, moduleCode: string, pageCode: string) {
    return this._decoration.getAdminDecorationTemplateBinding(tenantId, moduleCode, pageCode)
  }

  getAdminDecorationBusinessTemplateVersions(tenantId: number, moduleCode: string, templateCode: string) {
    return this._decoration.getAdminDecorationBusinessTemplateVersions(tenantId, moduleCode, templateCode)
  }

  updateAdminDecorationBusinessTemplateVersionStatus(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: UpdateDecorationTemplateVersionStatusRequest
  ) {
    return this._decoration.updateAdminDecorationBusinessTemplateVersionStatus(tenantId, moduleCode, templateCode, templateVersion, body)
  }

  installAdminDecorationBusinessTemplateVersion(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ): Promise<DecorationPage> {
    return this._decoration.installAdminDecorationBusinessTemplateVersion(tenantId, moduleCode, templateCode, templateVersion, body)
  }

  previewAdminDecorationTemplateUpgrade(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: PreviewDecorationTemplateUpgradeRequest
  ) {
    return this._decoration.previewAdminDecorationTemplateUpgrade(tenantId, moduleCode, pageCode, body)
  }

  applyAdminDecorationTemplateUpgrade(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: ApplyDecorationTemplateUpgradeRequest
  ): Promise<DecorationPage> {
    return this._decoration.applyAdminDecorationTemplateUpgrade(tenantId, moduleCode, pageCode, body)
  }

  // ==================== Fulfillment ====================
  getSaasFulfillmentProfiles() {
    return this._fulfillment.getSaasFulfillmentProfiles()
  }
  saveSaasFulfillmentProfile(body: Parameters<FulfillmentApiModule['saveSaasFulfillmentProfile']>[0]) {
    return this._fulfillment.saveSaasFulfillmentProfile(body)
  }
  disableSaasFulfillmentProfile(profileId: number) {
    return this._fulfillment.disableSaasFulfillmentProfile(profileId)
  }
  getSaasFulfillmentTypes(moduleCode: string) {
    return this._fulfillment.getSaasFulfillmentTypes(moduleCode)
  }
  getSaasFulfillmentStatus(orderId: number) {
    return this._fulfillment.getSaasFulfillmentStatus(orderId)
  }
  getClientFulfillmentStatus(orderId: number) {
    return this._fulfillment.getClientFulfillmentStatus(orderId)
  }
  getSaasProductFulfillmentOptions(spuId: number, skuId?: number | null) {
    return this._fulfillment.getSaasProductFulfillmentOptions(spuId, skuId)
  }
  saveSaasProductFulfillmentOptions(
    spuId: number,
    options: Parameters<FulfillmentApiModule['saveSaasProductFulfillmentOptions']>[1],
    skuId?: number | null
  ) {
    return this._fulfillment.saveSaasProductFulfillmentOptions(spuId, options, skuId)
  }

  // ==================== Mall & Catalog ====================
  getSaasProductCategories(query?: Parameters<MallApiModule['getSaasProductCategories']>[0]) {
    return this._mall.getSaasProductCategories(query)
  }
  createSaasProductCategory(body: Parameters<MallApiModule['createSaasProductCategory']>[0]) {
    return this._mall.createSaasProductCategory(body)
  }
  updateSaasProductCategory(categoryId: number, body: Parameters<MallApiModule['updateSaasProductCategory']>[1]) {
    return this._mall.updateSaasProductCategory(categoryId, body)
  }
  updateSaasProductCategoryStatus(categoryId: number, enabled: boolean) {
    return this._mall.updateSaasProductCategoryStatus(categoryId, enabled)
  }
  getSaasProductBrands(query?: Parameters<MallApiModule['getSaasProductBrands']>[0]) {
    return this._mall.getSaasProductBrands(query)
  }
  createSaasProductBrand(body: Parameters<MallApiModule['createSaasProductBrand']>[0]) {
    return this._mall.createSaasProductBrand(body)
  }
  updateSaasProductBrand(brandId: number, body: Parameters<MallApiModule['updateSaasProductBrand']>[1]) {
    return this._mall.updateSaasProductBrand(brandId, body)
  }
  updateSaasProductBrandStatus(brandId: number, enabled: boolean) {
    return this._mall.updateSaasProductBrandStatus(brandId, enabled)
  }
  getSaasProductCatalog(query?: Parameters<MallApiModule['getSaasProductCatalog']>[0]) {
    return this._mall.getSaasProductCatalog(query)
  }
  getSaasProductDetail(productId: number) {
    return this._mall.getSaasProductDetail(productId)
  }
  getSaasMallWorkbenchScopeOptions() {
    return this._mall.getSaasMallWorkbenchScopeOptions()
  }
  createSaasProductCatalog(body: Parameters<MallApiModule['createSaasProductCatalog']>[0]) {
    return this._mall.createSaasProductCatalog(body)
  }
  createSaasProductCatalogWithInitialSku(body: Parameters<MallApiModule['createSaasProductCatalogWithInitialSku']>[0]) {
    return this._mall.createSaasProductCatalogWithInitialSku(body)
  }
  updateSaasProductCatalog(productId: number, body: Parameters<MallApiModule['updateSaasProductCatalog']>[1]) {
    return this._mall.updateSaasProductCatalog(productId, body)
  }
  getSaasProductStoreIds(productId: number) {
    return this._mall.getSaasProductStoreIds(productId)
  }
  replaceSaasProductStoreIds(productId: number, storeIds: number[]) {
    return this._mall.replaceSaasProductStoreIds(productId, storeIds)
  }
  getSaasSubmittedMallProductReviews(shopId: number | null | undefined, cursor: string | null, limit: number) {
    return this._mall.getSaasSubmittedMallProductReviews(shopId, cursor, limit)
  }
  getSaasMallProductReviews(shopId?: number | null) {
    return this._mall.getSaasMallProductReviews(shopId)
  }
  getSaasMallShopFulfillmentConfig(shopId: number, options?: { signal?: AbortSignal }) {
    return this._mall.getSaasMallShopFulfillmentConfig(shopId, options)
  }
  updateSaasMallShopFulfillmentConfig(shopId: number, body: Parameters<MallApiModule['updateSaasMallShopFulfillmentConfig']>[1]) {
    return this._mall.updateSaasMallShopFulfillmentConfig(shopId, body)
  }
  updateSaasProductCatalogStatus(productId: number, body: Parameters<MallApiModule['updateSaasProductCatalogStatus']>[1]) {
    return this._mall.updateSaasProductCatalogStatus(productId, body)
  }
  getSaasProductSkus(productId: number, query?: Parameters<MallApiModule['getSaasProductSkus']>[1]) {
    return this._mall.getSaasProductSkus(productId, query)
  }
  createSaasProductCatalogSku(productId: number, body: Parameters<MallApiModule['createSaasProductCatalogSku']>[1]) {
    return this._mall.createSaasProductCatalogSku(productId, body)
  }
  updateSaasProductCatalogSku(productId: number, skuId: number, body: Parameters<MallApiModule['updateSaasProductCatalogSku']>[2]) {
    return this._mall.updateSaasProductCatalogSku(productId, skuId, body)
  }
  updateSaasProductCatalogSkuStatus(productId: number, skuId: number, body: Parameters<MallApiModule['updateSaasProductCatalogSkuStatus']>[2]) {
    return this._mall.updateSaasProductCatalogSkuStatus(productId, skuId, body)
  }
  getSaasMallProducts(query: Parameters<MallApiModule['getSaasMallProducts']>[0]) {
    return this._mall.getSaasMallProducts(query)
  }
  getAdminProducts(query: Parameters<MallApiModule['getAdminProducts']>[0]) {
    return this._mall.getAdminProducts(query)
  }
  createAdminProduct(body: Parameters<MallApiModule['createAdminProduct']>[0]) {
    return this._mall.createAdminProduct(body)
  }
  updateAdminProduct(productId: number, body: Parameters<MallApiModule['updateAdminProduct']>[1]) {
    return this._mall.updateAdminProduct(productId, body)
  }
  updateAdminProductStatus(productId: number, body: Parameters<MallApiModule['updateAdminProductStatus']>[1]) {
    return this._mall.updateAdminProductStatus(productId, body)
  }
  createAdminProductSku(productId: number, body: Parameters<MallApiModule['createAdminProductSku']>[1]) {
    return this._mall.createAdminProductSku(productId, body)
  }
  updateAdminProductSku(productId: number, skuId: number, body: Parameters<MallApiModule['updateAdminProductSku']>[2]) {
    return this._mall.updateAdminProductSku(productId, skuId, body)
  }
  getAdminOrders(query: Parameters<MallApiModule['getAdminOrders']>[0]) {
    return this._mall.getAdminOrders(query)
  }
  getAdminFeedEntries(query?: Parameters<FeedApiModule['getAdminFeedEntries']>[0]) {
    return this._feed.getAdminFeedEntries(query)
  }
  getAdminOrder(tenantId: number, orderId: number) {
    return this._mall.getAdminOrder(tenantId, orderId)
  }
  markAdminOrderPaid(tenantId: number, orderId: number, body: Parameters<MallApiModule['markAdminOrderPaid']>[2]) {
    return this._mall.markAdminOrderPaid(tenantId, orderId, body)
  }
  markAdminOrderFulfilling(tenantId: number, orderId: number) {
    return this._mall.markAdminOrderFulfilling(tenantId, orderId)
  }
  cancelAdminOrder(tenantId: number, orderId: number, body?: Parameters<MallApiModule['cancelAdminOrder']>[2]) {
    return this._mall.cancelAdminOrder(tenantId, orderId, body)
  }
  completeAdminOrder(tenantId: number, orderId: number) {
    return this._mall.completeAdminOrder(tenantId, orderId)
  }
  getSaasMallCommonOrders(
    query: Parameters<MallApiModule['getSaasMallCommonOrders']>[0],
    cursor: Parameters<MallApiModule['getSaasMallCommonOrders']>[1],
    limit: Parameters<MallApiModule['getSaasMallCommonOrders']>[2],
  ) {
    return this._mall.getSaasMallCommonOrders(query, cursor, limit)
  }
  getMallProduct(productId: number) {
    return this._mall.getMallProduct(productId)
  }
  listMallProducts(query: Parameters<MallApiModule['listMallProducts']>[0]) {
    return this._mall.listMallProducts(query)
  }
  createMallSku(productId: number, body: Parameters<MallApiModule['createMallSku']>[1]) {
    return this._mall.createMallSku(productId, body)
  }
  updateMallSkuStatus(productId: number, skuId: number, body: Parameters<MallApiModule['updateMallSkuStatus']>[2]) {
    return this._mall.updateMallSkuStatus(productId, skuId, body)
  }
  /** P1 T8 买家统一订单游标列表（未支付恢复传 status: 'PENDING_PAY'）。 */
  listCommonOrders(query: Parameters<MallApiModule['listCommonOrders']>[0]) {
    return this._mall.listCommonOrders(query)
  }
  /** P1 T8 买家统一订单详情（含多行 items 投影）。 */
  getCommonOrder(orderId: Parameters<MallApiModule['getCommonOrder']>[0]) {
    return this._mall.getCommonOrder(orderId)
  }
  /** P1 T8 买家多行统一单提交（同店多 SKU 合单）。 */
  submitClientOrder(body: Parameters<MallApiModule['submitClientOrder']>[0]) {
    return this._mall.submitClientOrder(body)
  }
  getSaasMallCommonOrder(orderId: number) {
    return this._mall.getSaasMallCommonOrder(orderId)
  }
  getSaasMallCommonOrderTransactions(orderId: number) {
    return this._mall.getSaasMallCommonOrderTransactions(orderId)
  }
  getSaasMallCommonOrderTransaction(orderId: number, transactionId: number) {
    return this._mall.getSaasMallCommonOrderTransaction(orderId, transactionId)
  }

  // ==================== Alumni ====================
  getSaasAlumniWorkbench() {
    return this._alumni.getSaasAlumniWorkbench()
  }
  getSaasAlumniDashboard() {
    return this._alumni.getSaasAlumniDashboard()
  }
  getSaasAlumniDecoration() {
    return this._alumni.getSaasAlumniDecoration()
  }
  saveSaasAlumniDecoration(body: Parameters<AlumniApiModule['saveSaasAlumniDecoration']>[0]) {
    return this._alumni.saveSaasAlumniDecoration(body)
  }
  getSaasAlumniProfiles(query?: Parameters<AlumniApiModule['getSaasAlumniProfiles']>[0]) {
    return this._alumni.getSaasAlumniProfiles(query)
  }
  getSaasAlumniProfilesPage(query?: Parameters<AlumniApiModule['getSaasAlumniProfilesPage']>[0]) {
    return this._alumni.getSaasAlumniProfilesPage(query)
  }
  getSaasAlumniProfileChangeLog(profileId: number | string, pageNum?: number, pageSize?: number) {
    return this._alumni.getSaasAlumniProfileChangeLog(profileId, pageNum, pageSize)
  }
  createSaasAlumniProfile(body: Parameters<AlumniApiModule['createSaasAlumniProfile']>[0]) {
    return this._alumni.createSaasAlumniProfile(body)
  }
  updateSaasAlumniProfile(profileId: number, body: Parameters<AlumniApiModule['updateSaasAlumniProfile']>[1]) {
    return this._alumni.updateSaasAlumniProfile(profileId, body)
  }
  deleteSaasAlumniProfile(profileId: number) {
    return this._alumni.deleteSaasAlumniProfile(profileId)
  }
  getSaasAlumniClasses() {
    return this._alumni.getSaasAlumniClasses()
  }
  createSaasAlumniClass(body: Parameters<AlumniApiModule['createSaasAlumniClass']>[0]) {
    return this._alumni.createSaasAlumniClass(body)
  }
  updateSaasAlumniClass(classId: number, body: Parameters<AlumniApiModule['updateSaasAlumniClass']>[1]) {
    return this._alumni.updateSaasAlumniClass(classId, body)
  }
  getSaasAlumniGroups(query?: Parameters<AlumniApiModule['getSaasAlumniGroups']>[0]) {
    return this._alumni.getSaasAlumniGroups(query)
  }
  createSaasAlumniGroup(body: Parameters<AlumniApiModule['createSaasAlumniGroup']>[0]) {
    return this._alumni.createSaasAlumniGroup(body)
  }
  updateSaasAlumniGroup(groupId: number, body: Parameters<AlumniApiModule['updateSaasAlumniGroup']>[1]) {
    return this._alumni.updateSaasAlumniGroup(groupId, body)
  }
  issueSaasAlumniGroupQrCode(groupId: number) {
    return this._alumni.issueSaasAlumniGroupQrCode(groupId)
  }
  getSaasAlumniGroupMembers(groupId: number, pageNum?: number, pageSize?: number) {
    return this._alumni.getSaasAlumniGroupMembers(groupId, pageNum, pageSize)
  }
  addSaasAlumniGroupMember(groupId: number, body: Parameters<AlumniApiModule['addSaasAlumniGroupMember']>[1]) {
    return this._alumni.addSaasAlumniGroupMember(groupId, body)
  }
  getSaasAlumniGroupTypes() {
    return this._alumni.getSaasAlumniGroupTypes()
  }
  createSaasAlumniGroupType(body: Parameters<AlumniApiModule['createSaasAlumniGroupType']>[0]) {
    return this._alumni.createSaasAlumniGroupType(body)
  }
  updateSaasAlumniGroupType(typeId: number, body: Parameters<AlumniApiModule['updateSaasAlumniGroupType']>[1]) {
    return this._alumni.updateSaasAlumniGroupType(typeId, body)
  }
  deleteSaasAlumniGroupType(typeId: number) {
    return this._alumni.deleteSaasAlumniGroupType(typeId)
  }
  getSaasAlumniPoints(pageNum?: number, pageSize?: number) {
    return this._alumni.getSaasAlumniPoints(pageNum, pageSize)
  }
  getSaasAlumniCertifications() {
    return this._alumni.getSaasAlumniCertifications()
  }
  getSaasAlumniCertificationDetail(certId: number) {
    return this._alumni.getSaasAlumniCertificationDetail(certId)
  }
  approveSaasAlumniCertification(certId: number, body: Parameters<AlumniApiModule['approveSaasAlumniCertification']>[1]) {
    return this._alumni.approveSaasAlumniCertification(certId, body)
  }
  rejectSaasAlumniCertification(certId: number, body: Parameters<AlumniApiModule['rejectSaasAlumniCertification']>[1]) {
    return this._alumni.rejectSaasAlumniCertification(certId, body)
  }
  getSaasAlumniEvents() {
    return this._alumni.getSaasAlumniEvents()
  }
  createSaasAlumniEvent(body: Parameters<AlumniApiModule['createSaasAlumniEvent']>[0]) {
    return this._alumni.createSaasAlumniEvent(body)
  }
  updateSaasAlumniEvent(eventId: number, body: Parameters<AlumniApiModule['updateSaasAlumniEvent']>[1]) {
    return this._alumni.updateSaasAlumniEvent(eventId, body)
  }
  getSaasAlumniNews() {
    return this._alumni.getSaasAlumniNews()
  }
  createSaasAlumniNews(body: Parameters<AlumniApiModule['createSaasAlumniNews']>[0]) {
    return this._alumni.createSaasAlumniNews(body)
  }
  updateSaasAlumniNews(newsId: number, body: Parameters<AlumniApiModule['updateSaasAlumniNews']>[1]) {
    return this._alumni.updateSaasAlumniNews(newsId, body)
  }
  deleteSaasAlumniNews(newsId: number) {
    return this._alumni.deleteSaasAlumniNews(newsId)
  }
  getSaasAlumniPostsPage(query?: Parameters<AlumniApiModule['getSaasAlumniPostsPage']>[0]) {
    return this._alumni.getSaasAlumniPostsPage(query)
  }
  getSaasAlumniPost(postId: number) {
    return this._alumni.getSaasAlumniPost(postId)
  }
  updateSaasAlumniPost(postId: number, body: Parameters<AlumniApiModule['updateSaasAlumniPost']>[1]) {
    return this._alumni.updateSaasAlumniPost(postId, body)
  }
  deleteSaasAlumniPost(postId: number) {
    return this._alumni.deleteSaasAlumniPost(postId)
  }
  getSaasAlumniServiceApplications(query?: Parameters<AlumniApiModule['getSaasAlumniServiceApplications']>[0]) {
    return this._alumni.getSaasAlumniServiceApplications(query)
  }
  createSaasAlumniServiceApplication(body: Parameters<AlumniApiModule['createSaasAlumniServiceApplication']>[0]) {
    return this._alumni.createSaasAlumniServiceApplication(body)
  }
  updateSaasAlumniServiceApplicationStatus(applicationId: number, body: Parameters<AlumniApiModule['updateSaasAlumniServiceApplicationStatus']>[1]) {
    return this._alumni.updateSaasAlumniServiceApplicationStatus(applicationId, body)
  }
  getSaasAlumniCatalogItems(query?: Parameters<AlumniApiModule['getSaasAlumniCatalogItems']>[0]) {
    return this._alumni.getSaasAlumniCatalogItems(query)
  }
  createSaasAlumniCatalogItem(body: Parameters<AlumniApiModule['createSaasAlumniCatalogItem']>[0]) {
    return this._alumni.createSaasAlumniCatalogItem(body)
  }
  updateSaasAlumniCatalogItem(itemId: number, body: Parameters<AlumniApiModule['updateSaasAlumniCatalogItem']>[1]) {
    return this._alumni.updateSaasAlumniCatalogItem(itemId, body)
  }
  deleteSaasAlumniCatalogItem(itemId: number) {
    return this._alumni.deleteSaasAlumniCatalogItem(itemId)
  }
  getSaasAlumniMedalConfigs() {
    return this._alumni.getSaasAlumniMedalConfigs()
  }
  getSaasAlumniMedalConfig(id: number) {
    return this._alumni.getSaasAlumniMedalConfig(id)
  }
  createSaasAlumniMedalConfig(body: Parameters<AlumniApiModule['createSaasAlumniMedalConfig']>[0]) {
    return this._alumni.createSaasAlumniMedalConfig(body)
  }
  updateSaasAlumniMedalConfig(id: number, body: Parameters<AlumniApiModule['updateSaasAlumniMedalConfig']>[1]) {
    return this._alumni.updateSaasAlumniMedalConfig(id, body)
  }
  deleteSaasAlumniMedalConfig(id: number) {
    return this._alumni.deleteSaasAlumniMedalConfig(id)
  }
  getSaasAlumniMedalAwards(query?: Parameters<AlumniApiModule['getSaasAlumniMedalAwards']>[0]) {
    return this._alumni.getSaasAlumniMedalAwards(query)
  }
  createSaasAlumniMedalAward(body: Parameters<AlumniApiModule['createSaasAlumniMedalAward']>[0]) {
    return this._alumni.createSaasAlumniMedalAward(body)
  }
  deleteSaasAlumniMedalAward(id: number) {
    return this._alumni.deleteSaasAlumniMedalAward(id)
  }
  getSaasAlumniCards(query?: Parameters<AlumniApiModule['getSaasAlumniCards']>[0]) {
    return this._alumni.getSaasAlumniCards(query)
  }
  getSaasAlumniCard(id: number) {
    return this._alumni.getSaasAlumniCard(id)
  }
  createSaasAlumniCard(body: Parameters<AlumniApiModule['createSaasAlumniCard']>[0]) {
    return this._alumni.createSaasAlumniCard(body)
  }
  updateSaasAlumniCard(id: number, body: Parameters<AlumniApiModule['updateSaasAlumniCard']>[1]) {
    return this._alumni.updateSaasAlumniCard(id, body)
  }
  getSaasAlumniAlbums(query?: Parameters<AlumniApiModule['getSaasAlumniAlbums']>[0]) {
    return this._alumni.getSaasAlumniAlbums(query)
  }
  getSaasAlumniAlbum(albumId: number) {
    return this._alumni.getSaasAlumniAlbum(albumId)
  }
  createSaasAlumniAlbum(body: Parameters<AlumniApiModule['createSaasAlumniAlbum']>[0]) {
    return this._alumni.createSaasAlumniAlbum(body)
  }
  updateSaasAlumniAlbum(albumId: number, body: Parameters<AlumniApiModule['updateSaasAlumniAlbum']>[1]) {
    return this._alumni.updateSaasAlumniAlbum(albumId, body)
  }
  deleteSaasAlumniAlbum(albumId: number) {
    return this._alumni.deleteSaasAlumniAlbum(albumId)
  }
  getSaasAlumniPhotos(albumId: number, query?: Parameters<AlumniApiModule['getSaasAlumniPhotos']>[1]) {
    return this._alumni.getSaasAlumniPhotos(albumId, query)
  }
  createSaasAlumniPhoto(albumId: number, body: Parameters<AlumniApiModule['createSaasAlumniPhoto']>[1]) {
    return this._alumni.createSaasAlumniPhoto(albumId, body)
  }
  updateSaasAlumniPhoto(photoId: number, body: Parameters<AlumniApiModule['updateSaasAlumniPhoto']>[1]) {
    return this._alumni.updateSaasAlumniPhoto(photoId, body)
  }
  deleteSaasAlumniPhoto(photoId: number) {
    return this._alumni.deleteSaasAlumniPhoto(photoId)
  }
  getSaasAlumniFeedbacks(query?: Parameters<AlumniApiModule['getSaasAlumniFeedbacks']>[0]) {
    return this._alumni.getSaasAlumniFeedbacks(query)
  }
  getSaasAlumniFeedback(id: number) {
    return this._alumni.getSaasAlumniFeedback(id)
  }
  replySaasAlumniFeedback(id: number, body: Parameters<AlumniApiModule['replySaasAlumniFeedback']>[1]) {
    return this._alumni.replySaasAlumniFeedback(id, body)
  }
  closeSaasAlumniFeedback(id: number) {
    return this._alumni.closeSaasAlumniFeedback(id)
  }
  getSaasAlumniNamecards(query?: Parameters<AlumniApiModule['getSaasAlumniNamecards']>[0]) {
    return this._alumni.getSaasAlumniNamecards(query)
  }
  getSaasAlumniNamecard(id: number) {
    return this._alumni.getSaasAlumniNamecard(id)
  }
  getSaasAlumniStatsAggregate() {
    return this._alumni.getSaasAlumniStatsAggregate()
  }

  // ==================== Guoxue ====================
  getSaasGuoxueWorkbench() {
    return this._guoxue.getSaasGuoxueWorkbench()
  }
  getSaasGuoxueAccountAbout() {
    return this._guoxue.getSaasGuoxueAccountAbout()
  }
  saveSaasGuoxueAccountAbout(body: Parameters<GuoxueApiModule['saveSaasGuoxueAccountAbout']>[0]) {
    return this._guoxue.saveSaasGuoxueAccountAbout(body)
  }
  getSaasGuoxueAccountUsers(query?: Parameters<GuoxueApiModule['getSaasGuoxueAccountUsers']>[0]) {
    return this._guoxue.getSaasGuoxueAccountUsers(query)
  }
  getSaasGuoxueWalletAccounts() {
    return this._guoxue.getSaasGuoxueWalletAccounts()
  }
  getSaasGuoxueWalletTransactions() {
    return this._guoxue.getSaasGuoxueWalletTransactions()
  }
  getSaasGuoxueWalletRecharges() {
    return this._guoxue.getSaasGuoxueWalletRecharges()
  }
  getSaasGuoxueCoupons() {
    return this._guoxue.getSaasGuoxueCoupons()
  }
  getSaasGuoxueDailySignRecords(query?: Parameters<GuoxueApiModule['getSaasGuoxueDailySignRecords']>[0]) {
    return this._guoxue.getSaasGuoxueDailySignRecords(query)
  }
  getSaasGuoxueDailySignStats() {
    return this._guoxue.getSaasGuoxueDailySignStats()
  }
  getSaasGuoxueToolRecords(query?: Parameters<GuoxueApiModule['getSaasGuoxueToolRecords']>[0]) {
    return this._guoxue.getSaasGuoxueToolRecords(query)
  }
  getSaasGuoxueMessages(query?: Parameters<GuoxueApiModule['getSaasGuoxueMessages']>[0]) {
    return this._guoxue.getSaasGuoxueMessages(query)
  }
  getSaasGuoxueMessageStats() {
    return this._guoxue.getSaasGuoxueMessageStats()
  }
  sendSaasGuoxueMessage(body: Parameters<GuoxueApiModule['sendSaasGuoxueMessage']>[0]) {
    return this._guoxue.sendSaasGuoxueMessage(body)
  }
  revokeSaasGuoxueMessage(messageId: number) {
    return this._guoxue.revokeSaasGuoxueMessage(messageId)
  }
  getSaasGuoxueConsultExperts(query?: Parameters<GuoxueApiModule['getSaasGuoxueConsultExperts']>[0]) {
    return this._guoxue.getSaasGuoxueConsultExperts(query)
  }
  saveSaasGuoxueConsultExpert(body: Parameters<GuoxueApiModule['saveSaasGuoxueConsultExpert']>[0]) {
    return this._guoxue.saveSaasGuoxueConsultExpert(body)
  }
  getSaasGuoxueConsultServices(query?: Parameters<GuoxueApiModule['getSaasGuoxueConsultServices']>[0]) {
    return this._guoxue.getSaasGuoxueConsultServices(query)
  }
  saveSaasGuoxueConsultService(body: Parameters<GuoxueApiModule['saveSaasGuoxueConsultService']>[0]) {
    return this._guoxue.saveSaasGuoxueConsultService(body)
  }
  getSaasGuoxueConsultReviews(query?: Parameters<GuoxueApiModule['getSaasGuoxueConsultReviews']>[0]) {
    return this._guoxue.getSaasGuoxueConsultReviews(query)
  }
  updateSaasGuoxueConsultReview(reviewId: number, body: Parameters<GuoxueApiModule['updateSaasGuoxueConsultReview']>[1]) {
    return this._guoxue.updateSaasGuoxueConsultReview(reviewId, body)
  }
  getSaasGuoxueConsultOrders(query?: Parameters<GuoxueApiModule['getSaasGuoxueConsultOrders']>[0]) {
    return this._guoxue.getSaasGuoxueConsultOrders(query)
  }
  getSaasGuoxueConsultSchedules(query?: Parameters<GuoxueApiModule['getSaasGuoxueConsultSchedules']>[0]) {
    return this._guoxue.getSaasGuoxueConsultSchedules(query)
  }
  updateSaasGuoxueConsultSchedule(expertId: number, body: Parameters<GuoxueApiModule['updateSaasGuoxueConsultSchedule']>[1]) {
    return this._guoxue.updateSaasGuoxueConsultSchedule(expertId, body)
  }
  getSaasGuoxueLiveDashboard() {
    return this._guoxue.getSaasGuoxueLiveDashboard()
  }
  getSaasGuoxueLiveProviderConfig() {
    return this._guoxue.getSaasGuoxueLiveProviderConfig()
  }
  saveSaasGuoxueLiveProviderConfig(body: Parameters<GuoxueApiModule['saveSaasGuoxueLiveProviderConfig']>[0]) {
    return this._guoxue.saveSaasGuoxueLiveProviderConfig(body)
  }
  updateSaasGuoxueLiveProviderConfig(configId: number, body: Parameters<GuoxueApiModule['updateSaasGuoxueLiveProviderConfig']>[1]) {
    return this._guoxue.updateSaasGuoxueLiveProviderConfig(configId, body)
  }
  testSaasGuoxueLiveProviderConfig(body?: Parameters<GuoxueApiModule['testSaasGuoxueLiveProviderConfig']>[0]) {
    return this._guoxue.testSaasGuoxueLiveProviderConfig(body)
  }
  getSaasGuoxueLiveRooms(query?: Parameters<GuoxueApiModule['getSaasGuoxueLiveRooms']>[0]) {
    return this._guoxue.getSaasGuoxueLiveRooms(query)
  }
  createSaasGuoxueLiveRoom(body: Parameters<GuoxueApiModule['createSaasGuoxueLiveRoom']>[0]) {
    return this._guoxue.createSaasGuoxueLiveRoom(body)
  }
  updateSaasGuoxueLiveRoom(roomId: number, body: Parameters<GuoxueApiModule['updateSaasGuoxueLiveRoom']>[1]) {
    return this._guoxue.updateSaasGuoxueLiveRoom(roomId, body)
  }
  startSaasGuoxueLiveRoom(roomId: number) {
    return this._guoxue.startSaasGuoxueLiveRoom(roomId)
  }
  pauseSaasGuoxueLiveRoom(roomId: number) {
    return this._guoxue.pauseSaasGuoxueLiveRoom(roomId)
  }
  endSaasGuoxueLiveRoom(roomId: number) {
    return this._guoxue.endSaasGuoxueLiveRoom(roomId)
  }
  generateSaasGuoxueLivePushAuth(roomId: number) {
    return this._guoxue.generateSaasGuoxueLivePushAuth(roomId)
  }
  createSaasGuoxueLiveHostSession(roomId: number) {
    return this._guoxue.createSaasGuoxueLiveHostSession(roomId)
  }
  getSaasGuoxueLiveMicApplications(query?: Parameters<GuoxueApiModule['getSaasGuoxueLiveMicApplications']>[0]) {
    return this._guoxue.getSaasGuoxueLiveMicApplications(query)
  }
  acceptSaasGuoxueLiveMic(roomId: number, orderNo: string) {
    return this._guoxue.acceptSaasGuoxueLiveMic(roomId, orderNo)
  }
  rejectSaasGuoxueLiveMic(roomId: number, orderNo: string, reason?: string) {
    return this._guoxue.rejectSaasGuoxueLiveMic(roomId, orderNo, reason)
  }
  hangupSaasGuoxueLiveMic(roomId: number, orderNo: string, reason?: string) {
    return this._guoxue.hangupSaasGuoxueLiveMic(roomId, orderNo, reason)
  }
  getSaasGuoxueLiveRoomMessages(roomId: number, query?: Parameters<GuoxueApiModule['getSaasGuoxueLiveRoomMessages']>[1]) {
    return this._guoxue.getSaasGuoxueLiveRoomMessages(roomId, query)
  }
  sendSaasGuoxueLiveSystemMessage(roomId: number, content: string) {
    return this._guoxue.sendSaasGuoxueLiveSystemMessage(roomId, content)
  }
  auditSaasGuoxueLiveMessage(messageId: string, auditStatus: string) {
    return this._guoxue.auditSaasGuoxueLiveMessage(messageId, auditStatus)
  }
  getSaasGuoxueLiveGifts() {
    return this._guoxue.getSaasGuoxueLiveGifts()
  }
  getSaasGuoxueLiveExceptions(query?: Parameters<GuoxueApiModule['getSaasGuoxueLiveExceptions']>[0]) {
    return this._guoxue.getSaasGuoxueLiveExceptions(query)
  }
  resolveSaasGuoxueLiveException(exceptionId: number, note?: string) {
    return this._guoxue.resolveSaasGuoxueLiveException(exceptionId, note)
  }
  getSaasGuoxueTeacherApplications(query?: Parameters<GuoxueApiModule['getSaasGuoxueTeacherApplications']>[0]) {
    return this._guoxue.getSaasGuoxueTeacherApplications(query)
  }
  getSaasGuoxueTeacherApplication(applicationId: number) {
    return this._guoxue.getSaasGuoxueTeacherApplication(applicationId)
  }
  approveSaasGuoxueTeacherApplication(applicationId: number) {
    return this._guoxue.approveSaasGuoxueTeacherApplication(applicationId)
  }
  rejectSaasGuoxueTeacherApplication(applicationId: number, reason: string) {
    return this._guoxue.rejectSaasGuoxueTeacherApplication(applicationId, reason)
  }
  getSaasGuoxueTeacherProfiles(query?: Parameters<GuoxueApiModule['getSaasGuoxueTeacherProfiles']>[0]) {
    return this._guoxue.getSaasGuoxueTeacherProfiles(query)
  }
  suspendSaasGuoxueTeacherProfile(profileId: number, reason: string) {
    return this._guoxue.suspendSaasGuoxueTeacherProfile(profileId, reason)
  }
  resumeSaasGuoxueTeacherProfile(profileId: number) {
    return this._guoxue.resumeSaasGuoxueTeacherProfile(profileId)
  }
  getSaasGuoxueTeacherLiveSummary(profileId: number) {
    return this._guoxue.getSaasGuoxueTeacherLiveSummary(profileId)
  }
  getClientGuoxueExperts(query?: Parameters<GuoxueApiModule['getClientGuoxueExperts']>[0]) {
    return this._guoxue.getClientGuoxueExperts(query)
  }
  getClientGuoxueExpert(expertId: number) {
    return this._guoxue.getClientGuoxueExpert(expertId)
  }
  getClientGuoxueExpertServices(expertId: number) {
    return this._guoxue.getClientGuoxueExpertServices(expertId)
  }
  getClientGuoxueExpertReviews(expertId: number, query?: Parameters<GuoxueApiModule['getClientGuoxueExpertReviews']>[1]) {
    return this._guoxue.getClientGuoxueExpertReviews(expertId, query)
  }
  toggleClientGuoxueExpertFollow(expertId: number) {
    return this._guoxue.toggleClientGuoxueExpertFollow(expertId)
  }
  createClientGuoxueConsultOrder(body: Parameters<GuoxueApiModule['createClientGuoxueConsultOrder']>[0]) {
    return this._guoxue.createClientGuoxueConsultOrder(body)
  }

  // ==================== ERP ====================
  getSaasErpCommonOrders(query?: Parameters<ErpApiModule['getSaasErpCommonOrders']>[0]) {
    return this._erp.getSaasErpCommonOrders(query)
  }
  getSaasErpCommonOrder(orderId: number) {
    return this._erp.getSaasErpCommonOrder(orderId)
  }
  markSaasErpCommonOrderPaid(orderId: number, body: Parameters<ErpApiModule['markSaasErpCommonOrderPaid']>[1]) {
    return this._erp.markSaasErpCommonOrderPaid(orderId, body)
  }
  markSaasErpCommonOrderFulfilling(orderId: number) {
    return this._erp.markSaasErpCommonOrderFulfilling(orderId)
  }
  cancelSaasErpCommonOrder(orderId: number, body?: Parameters<ErpApiModule['cancelSaasErpCommonOrder']>[1]) {
    return this._erp.cancelSaasErpCommonOrder(orderId, body)
  }
  completeSaasErpCommonOrder(orderId: number) {
    return this._erp.completeSaasErpCommonOrder(orderId)
  }

  // ==================== Member ====================
  queryMemberCapabilityMembers(query?: Parameters<MemberApiModule['queryMemberCapabilityMembers']>[0]) {
    return this._member.queryMemberCapabilityMembers(query)
  }
  queryMemberCapabilityBindings(query?: Parameters<MemberApiModule['queryMemberCapabilityBindings']>[0]) {
    return this._member.queryMemberCapabilityBindings(query)
  }
  queryMemberCapabilityPointAccounts(query?: Parameters<MemberApiModule['queryMemberCapabilityPointAccounts']>[0]) {
    return this._member.queryMemberCapabilityPointAccounts(query)
  }
  queryMemberCapabilityPointLedgers(query?: Parameters<MemberApiModule['queryMemberCapabilityPointLedgers']>[0]) {
    return this._member.queryMemberCapabilityPointLedgers(query)
  }
  queryMemberCapabilityWalletAccounts(query?: Parameters<MemberApiModule['queryMemberCapabilityWalletAccounts']>[0]) {
    return this._member.queryMemberCapabilityWalletAccounts(query)
  }
  queryMemberCapabilityWalletLedgers(query?: Parameters<MemberApiModule['queryMemberCapabilityWalletLedgers']>[0]) {
    return this._member.queryMemberCapabilityWalletLedgers(query)
  }
  queryMemberCapabilityBenefits(query?: Parameters<MemberApiModule['queryMemberCapabilityBenefits']>[0]) {
    return this._member.queryMemberCapabilityBenefits(query)
  }
  queryMemberCapabilityBenefitLedgers(query?: Parameters<MemberApiModule['queryMemberCapabilityBenefitLedgers']>[0]) {
    return this._member.queryMemberCapabilityBenefitLedgers(query)
  }
  queryMemberCapabilityEvents(query?: Parameters<MemberApiModule['queryMemberCapabilityEvents']>[0]) {
    return this._member.queryMemberCapabilityEvents(query)
  }
  queryMemberCapabilityRules(query?: Parameters<MemberApiModule['queryMemberCapabilityRules']>[0]) {
    return this._member.queryMemberCapabilityRules(query)
  }
  upsertMemberCapabilityRule(body: Parameters<MemberApiModule['upsertMemberCapabilityRule']>[0]) {
    return this._member.upsertMemberCapabilityRule(body)
  }
  updateMemberCapabilityRuleStatus(body: Parameters<MemberApiModule['updateMemberCapabilityRuleStatus']>[0]) {
    return this._member.updateMemberCapabilityRuleStatus(body)
  }
}
